/**
 * Audio Analysis Service
 * Provides efficient audio analysis for both full songs (metadata extraction)
 * and time-range analysis (for seeking/scrubbing)
 */

export interface AudioAnalysisResult {
    spectralProfile: number[];  // 24 frequency bands
    dominantFreqs: number[];    // Top 5 frequencies
    energy: {
        bass: number;
        mid: number;
        treble: number;
    };
    bpm?: number;
}

export interface TimeRangeAnalysis {
    frequencies: Float32Array;  // Mapped frequency data for visualization
    dominantFreq: number;
    energy: number;
}

class AudioAnalysisService {
    /**
     * Analyze full audio buffer for metadata extraction
     * Samples at key points throughout the song for efficiency
     */
    async analyzeAudioBuffer(audioBuffer: AudioBuffer): Promise<AudioAnalysisResult> {
        const sampleRate = audioBuffer.sampleRate;
        const duration = audioBuffer.duration;
        
        // Sample at 5 evenly spaced points + start/end for efficiency
        const samplePoints = Math.min(7, Math.max(3, Math.floor(duration / 10)));
        const samples: AudioAnalysisResult[] = [];
        
        for (let i = 0; i < samplePoints; i++) {
            const time = (duration * i) / (samplePoints - 1);
            const analysis = await this.analyzeTimeRange(audioBuffer, time, 0.5);
            
            // Convert time-range analysis to full analysis format
            samples.push({
                spectralProfile: this.extractSpectralProfile(analysis.frequencies),
                dominantFreqs: this.extractTopFrequencies(analysis.frequencies, 5),
                energy: this.calculateEnergyDistribution(analysis.frequencies, sampleRate)
            });
        }
        
        // Aggregate samples for overall song profile
        return this.aggregateAnalyses(samples);
    }

    /**
     * Analyze a specific time range in the audio
     * Used for seeking/scrubbing while paused
     */
    async analyzeTimeRange(
        audioBuffer: AudioBuffer,
        startTime: number,
        duration: number = 0.1
    ): Promise<TimeRangeAnalysis> {
        const sampleRate = audioBuffer.sampleRate;
        const channels = audioBuffer.numberOfChannels;
        const isStereo = channels >= 2;
        
        // Calculate sample range
        const startSample = Math.floor(startTime * sampleRate);
        const numSamples = Math.min(
            Math.floor(duration * sampleRate),
            audioBuffer.length - startSample
        );
        
        if (startSample >= audioBuffer.length || numSamples <= 0) {
            return this.getEmptyAnalysis();
        }
        
        // Extract audio data for the time range
        const leftData = new Float32Array(numSamples);
        const rightData = isStereo ? new Float32Array(numSamples) : leftData;
        
        audioBuffer.copyFromChannel(leftData, 0, startSample);
        if (isStereo) {
            audioBuffer.copyFromChannel(rightData, 1, startSample);
        }
        
        // Perform FFT analysis
        const fftSize = Math.pow(2, Math.floor(Math.log2(Math.min(numSamples, 8192))));
        const leftFreqs = this.performFFT(leftData.slice(0, fftSize));
        const rightFreqs = isStereo ? this.performFFT(rightData.slice(0, fftSize)) : leftFreqs;
        
        // Extract peaks and map to visualization format
        const frequencies = this.extractAndMapFrequencies(
            leftFreqs,
            rightFreqs,
            sampleRate,
            isStereo
        );
        
        const dominantFreq = this.findDominantFrequency(frequencies);
        const energy = this.calculateTotalEnergy(frequencies);
        
        return { frequencies, dominantFreq, energy };
    }

    /**
     * Simplified FFT implementation using Web Audio API style analysis
     */
    private performFFT(samples: Float32Array): Float32Array {
        const fftSize = samples.length;
        const frequencies = new Float32Array(fftSize / 2);
        
        // Perform FFT for each frequency bin
        for (let k = 0; k < frequencies.length; k++) {
            let real = 0;
            let imag = 0;
            
            for (let n = 0; n < fftSize; n++) {
                const angle = (2 * Math.PI * k * n) / fftSize;
                real += samples[n] * Math.cos(angle);
                imag -= samples[n] * Math.sin(angle);
            }
            
            // Calculate magnitude
            frequencies[k] = Math.sqrt(real * real + imag * imag) / fftSize;
        }
        
        return frequencies;
    }

    /**
     * Extract and map frequency peaks to visualization format
     */
    private extractAndMapFrequencies(
        leftFreqs: Float32Array,
        rightFreqs: Float32Array,
        sampleRate: number,
        isStereo: boolean
    ): Float32Array {
        const nyquist = sampleRate / 2;
        const binSize = nyquist / leftFreqs.length;
        const minAmplitude = 0.01; // Threshold for significant frequencies
        
        interface Peak {
            freq: number;
            amp: number;
            channel: number; // 0=both, 1=left, 2=right
        }
        
        const peaks: Peak[] = [];
        
        // Find peaks in left channel
        for (let i = 1; i < leftFreqs.length - 1; i++) {
            const amplitude = leftFreqs[i];
            
            if (amplitude > minAmplitude &&
                amplitude > leftFreqs[i - 1] &&
                amplitude > leftFreqs[i + 1]) {
                const frequency = i * binSize;
                
                if (frequency >= 20 && frequency <= 20000) {
                    let channel = 0;
                    
                    if (isStereo) {
                        const rightAmp = rightFreqs[i];
                        if (Math.abs(amplitude - rightAmp) > 0.3 * amplitude) {
                            channel = amplitude > rightAmp ? 1 : 2;
                        }
                    }
                    
                    peaks.push({ freq: frequency, amp: amplitude, channel });
                }
            }
        }
        
        // Find additional peaks in right channel (stereo only)
        if (isStereo) {
            for (let i = 1; i < rightFreqs.length - 1; i++) {
                const amplitude = rightFreqs[i];
                
                if (amplitude > minAmplitude &&
                    amplitude > rightFreqs[i - 1] &&
                    amplitude > rightFreqs[i + 1]) {
                    const frequency = i * binSize;
                    
                    if (frequency >= 20 && frequency <= 20000) {
                        const leftAmp = leftFreqs[i];
                        if (amplitude > leftAmp * 1.3) { // Right is significantly stronger
                            const alreadyExists = peaks.some(p =>
                                Math.abs(p.freq - frequency) / frequency < 0.05
                            );
                            if (!alreadyExists) {
                                peaks.push({ freq: frequency, amp: amplitude, channel: 2 });
                            }
                        }
                    }
                }
            }
        }
        
        // Sort by amplitude and take top peaks
        peaks.sort((a, b) => b.amp - a.amp);
        const topPeaks = peaks.slice(0, 15);
        
        // Convert to visualization format: [freq, amp, channel, ...]
        const result = new Float32Array(topPeaks.length * 3);
        topPeaks.forEach((peak, i) => {
            result[i * 3] = peak.freq;
            result[i * 3 + 1] = peak.amp;
            result[i * 3 + 2] = peak.channel;
        });
        
        return result;
    }

    /**
     * Extract spectral profile (24 frequency bands)
     */
    private extractSpectralProfile(frequencies: Float32Array): number[] {
        // 24 bands logarithmically spaced from 20Hz to 20kHz
        const bands = 24;
        const profile = new Array(bands).fill(0);
        const minFreq = 20;
        const maxFreq = 20000;
        
        for (let i = 0; i < frequencies.length; i += 3) {
            const freq = frequencies[i];
            const amp = frequencies[i + 1];
            
            if (freq >= minFreq && freq <= maxFreq) {
                // Calculate which band this frequency belongs to (log scale)
                const logFreq = Math.log10(freq / minFreq);
                const logRange = Math.log10(maxFreq / minFreq);
                const bandIndex = Math.floor((logFreq / logRange) * bands);
                
                if (bandIndex >= 0 && bandIndex < bands) {
                    profile[bandIndex] = Math.max(profile[bandIndex], amp);
                }
            }
        }
        
        return profile;
    }

    /**
     * Extract top N frequencies
     */
    private extractTopFrequencies(frequencies: Float32Array, count: number): number[] {
        const peaks: { freq: number; amp: number }[] = [];
        
        for (let i = 0; i < frequencies.length; i += 3) {
            peaks.push({ freq: frequencies[i], amp: frequencies[i + 1] });
        }
        
        peaks.sort((a, b) => b.amp - a.amp);
        return peaks.slice(0, count).map(p => p.freq);
    }

    /**
     * Calculate energy distribution across bass/mid/treble
     */
    private calculateEnergyDistribution(
        frequencies: Float32Array,
        sampleRate: number
    ): { bass: number; mid: number; treble: number } {
        let bassEnergy = 0;
        let midEnergy = 0;
        let trebleEnergy = 0;
        
        for (let i = 0; i < frequencies.length; i += 3) {
            const freq = frequencies[i];
            const amp = frequencies[i + 1];
            const energy = amp * amp;
            
            if (freq < 250) {
                bassEnergy += energy;
            } else if (freq < 4000) {
                midEnergy += energy;
            } else {
                trebleEnergy += energy;
            }
        }
        
        const totalEnergy = bassEnergy + midEnergy + trebleEnergy;
        
        if (totalEnergy === 0) {
            return { bass: 0, mid: 0, treble: 0 };
        }
        
        return {
            bass: bassEnergy / totalEnergy,
            mid: midEnergy / totalEnergy,
            treble: trebleEnergy / totalEnergy
        };
    }

    /**
     * Aggregate multiple analyses into a single profile
     */
    private aggregateAnalyses(samples: AudioAnalysisResult[]): AudioAnalysisResult {
        const numSamples = samples.length;
        
        // Average spectral profiles
        const spectralProfile = new Array(24).fill(0);
        samples.forEach(sample => {
            sample.spectralProfile.forEach((val, i) => {
                spectralProfile[i] += val / numSamples;
            });
        });
        
        // Collect all dominant frequencies and find most common
        const allFreqs: number[] = [];
        samples.forEach(sample => allFreqs.push(...sample.dominantFreqs));
        const freqCounts = new Map<number, number>();
        allFreqs.forEach(freq => {
            // Group similar frequencies (within 5%)
            const roundedFreq = Math.round(freq / 10) * 10;
            freqCounts.set(roundedFreq, (freqCounts.get(roundedFreq) || 0) + 1);
        });
        
        const dominantFreqs = Array.from(freqCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 5)
            .map(([freq]) => freq);
        
        // Average energy distribution
        const energy = {
            bass: samples.reduce((sum, s) => sum + s.energy.bass, 0) / numSamples,
            mid: samples.reduce((sum, s) => sum + s.energy.mid, 0) / numSamples,
            treble: samples.reduce((sum, s) => sum + s.energy.treble, 0) / numSamples
        };
        
        return { spectralProfile, dominantFreqs, energy };
    }

    /**
     * Find dominant frequency in analyzed data
     */
    private findDominantFrequency(frequencies: Float32Array): number {
        let maxAmp = 0;
        let dominantFreq = 0;
        
        for (let i = 0; i < frequencies.length; i += 3) {
            const amp = frequencies[i + 1];
            if (amp > maxAmp) {
                maxAmp = amp;
                dominantFreq = frequencies[i];
            }
        }
        
        return dominantFreq;
    }

    /**
     * Calculate total energy
     */
    private calculateTotalEnergy(frequencies: Float32Array): number {
        let total = 0;
        for (let i = 1; i < frequencies.length; i += 3) {
            const amp = frequencies[i];
            total += amp * amp;
        }
        return Math.sqrt(total);
    }

    /**
     * Return empty analysis result
     */
    private getEmptyAnalysis(): TimeRangeAnalysis {
        return {
            frequencies: new Float32Array(0),
            dominantFreq: 0,
            energy: 0
        };
    }
}

export default new AudioAnalysisService();
